/**
 * CAND-DISP-1 — Candidate Review state.
 *
 * One pending list, one in-flight disposition. No bulk selection, no queue
 * administration, no polling loop.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  disposeCandidate,
  listPendingCandidates,
  searchOrganizations,
} from './api';
import type {
  Disposition,
  DispositionResult,
  OrganizationOption,
  PendingCandidate,
} from './types';

export function useCandidateReview() {
  const [candidates, setCandidates] = useState<PendingCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DispositionResult | null>(null);
  const [matches, setMatches] = useState<OrganizationOption[]>([]);
  const [searching, setSearching] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCandidates(await listPendingCandidates());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) {
      setMatches([]);
      return;
    }
    setSearching(true);
    try {
      setMatches(await searchOrganizations(query));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
    }
  }, []);

  const dispose = useCallback(
    async (
      candidateOrganizationId: string,
      disposition: Disposition,
      canonicalOrganizationId?: string | null,
      reviewNotes?: string | null,
    ) => {
      setBusyId(candidateOrganizationId);
      setError(null);
      setResult(null);
      try {
        const outcome = await disposeCandidate({
          candidateOrganizationId,
          disposition,
          canonicalOrganizationId,
          reviewNotes,
        });
        setResult(outcome);
        await refresh();
        return outcome;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setBusyId(null);
      }
    },
    [refresh],
  );

  return {
    candidates,
    loading,
    busyId,
    error,
    result,
    matches,
    searching,
    refresh,
    search,
    dispose,
  };
}
